import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Trash2, Plus, Minus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import Pouch from "@/components/Pouch";

export default function CartDrawer() {
  const { open, setOpen, items, removeItem, updateQty, subtotal, discount, discountPct, shipping, total, promoCode, setPromoCode } = useCart();
  const nav = useNavigate();
  const [inputCode, setInputCode] = React.useState(promoCode || "");
  React.useEffect(() => { setInputCode(promoCode || ""); }, [promoCode]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70]" data-testid="cart-drawer">
      <div className="absolute inset-0 backdrop-blur-sm" style={{ background: "rgba(0,0,0,0.6)" }} onClick={() => setOpen(false)} />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[440px] border-l border-[var(--border)] flex flex-col" style={{ background: "var(--surface)" }}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--border)]">
          <div>
            <div className="label-mono">Your Cart</div>
            <div className="font-serif text-2xl mt-1">{items.length} {items.length === 1 ? "item" : "items"}</div>
          </div>
          <button onClick={() => setOpen(false)} data-testid="cart-close"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {items.length === 0 && (
            <div className="text-center py-20 text-[var(--text-secondary)]">
              <div className="font-serif text-3xl text-[var(--text)] mb-3">Empty cup</div>
              Fill it with something extraordinary.
            </div>
          )}
          {items.map((it) => (
            <div key={it._uid} className="flex gap-4 border border-[var(--border)] p-3 bg-[var(--surface)]" data-testid="cart-item">
              <div className="w-20 h-20 overflow-hidden shrink-0" style={{ background: "var(--surface-warm)" }}>
                <Pouch
                  blend={it.blend_snapshot || (it.is_custom ? { name: it.name, ...(it.custom_spec || {}) } : { name: it.name })}
                  weightLabel={`${it.weight_g}G`}
                />
              </div>
              <div className="flex-1">
                <div className="font-serif text-lg leading-tight">{it.name}</div>
                <div className="label-mono text-[10px] mt-1">
                  {it.weight_g}g · {it.grind} {it.is_custom && "· custom"}
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center border border-[var(--border)]">
                    <button className="px-2 py-1" onClick={() => updateQty(it._uid, it.quantity - 1)} data-testid="qty-dec"><Minus size={12} /></button>
                    <span className="font-mono text-xs px-3">{it.quantity}</span>
                    <button className="px-2 py-1" onClick={() => updateQty(it._uid, it.quantity + 1)} data-testid="qty-inc"><Plus size={12} /></button>
                  </div>
                  <div className="font-mono text-sm text-[var(--primary)]">₹{it.price_inr * it.quantity}</div>
                </div>
              </div>
              <button onClick={() => removeItem(it._uid)} className="text-[var(--text-muted)] hover:text-[var(--accent)]" data-testid="cart-remove">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[var(--border)] px-6 py-5 space-y-3">
            {/* Promo code */}
            <div className="flex gap-2 pb-2">
              <input
                value={inputCode}
                onChange={(e) => setInputCode(e.target.value.toUpperCase())}
                placeholder="Promo code (e.g. RUDRA5)"
                className="input-dark flex-1 !py-2 text-xs uppercase tracking-widest"
                data-testid="cart-promo-input"
              />
              <button
                type="button"
                onClick={() => setPromoCode(inputCode)}
                className="btn-outline !py-2 !px-3 !text-[10px]"
                data-testid="cart-apply-promo"
              >
                Apply
              </button>
            </div>
            <div className="flex justify-between label-mono"><span>Subtotal</span><span className="font-mono text-[var(--text)]">₹{subtotal}</span></div>
            {discount > 0 && (
              <div className="flex justify-between label-mono badge-blink" data-testid="cart-discount-row">
                <span className="text-[var(--ember)]">{promoCode.toUpperCase()} · {discountPct}% off</span>
                <span className="font-mono text-[var(--ember)]">− ₹{discount}</span>
              </div>
            )}
            <div className="flex justify-between label-mono"><span>Shipping</span><span className="font-mono text-[var(--text)]">{shipping === 0 ? "Free" : `₹${shipping}`}</span></div>
            <div className="flex justify-between text-[var(--text)] pt-2 border-t border-[var(--border)]">
              <span className="label-mono text-[var(--text)]">Total</span>
              <span className="font-mono text-lg text-[var(--primary-hover)]">₹{total}</span>
            </div>
            <button
              className="btn-primary w-full mt-2"
              onClick={() => { setOpen(false); nav("/checkout"); }}
              data-testid="cart-checkout-btn"
            >
              Proceed to Checkout
            </button>
            <Link to="/shop" onClick={() => setOpen(false)} className="block text-center label-mono text-[var(--text-secondary)] hover:text-[var(--primary)]">
              or keep exploring
            </Link>
          </div>
        )}
      </aside>
    </div>
  );
}
